import * as React from "react";
import { css } from "@emotion/react";
import { ToastCard } from "./toast-card";

export type ToastType = "success" | "info" | "warning" | "error";

export type Toast = {
  type?: ToastType;
  title: string;
  detail: string;
};

export type QueuedToast = Toast & {
  id: number;
  type: ToastType;
};

const duplicateWindowMs = 2000;
const maximumVisibleToasts = 4;

const ToastContext = React.createContext<(toast: Toast) => void>(
  () => undefined,
);

export const useToast = (): ((toast: Toast) => void) =>
  React.useContext(ToastContext);

export const ToastProvider = ({ children }: { children: React.ReactNode }) => {
  const [toasts, setToasts] = React.useState<QueuedToast[]>([]);
  const nextId = React.useRef(0);
  const recentToasts = React.useRef(new Map<string, number>());

  const dismiss = React.useCallback((id: number): void => {
    setToasts((items) => items.filter((item) => item.id !== id));
  }, []);

  const enqueue = React.useCallback((toast: Toast): void => {
    const normalized = { ...toast, type: toast.type ?? "success" } as const;
    const key = JSON.stringify([
      normalized.type,
      normalized.title,
      normalized.detail,
    ]);
    const now = Date.now();
    const lastShownAt = recentToasts.current.get(key);

    for (const [recentKey, shownAt] of recentToasts.current) {
      if (now - shownAt >= duplicateWindowMs) {
        recentToasts.current.delete(recentKey);
      }
    }

    if (lastShownAt !== undefined && now - lastShownAt < duplicateWindowMs) {
      return;
    }

    recentToasts.current.set(key, now);
    nextId.current += 1;
    setToasts((items) => [
      ...items.slice(-(maximumVisibleToasts - 1)),
      { ...normalized, id: nextId.current },
    ]);
  }, []);

  return (
    <ToastContext.Provider value={enqueue}>
      {children}
      <aside css={styles.stack} aria-label="通知">
        {toasts.map((toast) => (
          <ToastCard key={toast.id} toast={toast} onDismiss={dismiss} />
        ))}
      </aside>
    </ToastContext.Provider>
  );
};

const styles = {
  stack: css`
    position: fixed;
    top: 24px;
    right: 24px;
    z-index: 1000;
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: min(400px, calc(100vw - 48px));

    @media (max-width: 600px) {
      top: 16px;
      right: 16px;
      left: 16px;
      width: auto;
    }
  `,
};
